import { Component, input, model } from '@angular/core';
import { MemberStatuses, OrganizationStatuses, PaymentStatus, PropertyStatuses, UserStatuses, UserInvitationStatuses, ChargeStatus, BillingStatuses, EmailDeliveryStatus, ExpenseStatus } from '../../core/enums/data.enums';
import { EnumUtilities } from '../../core/utilities/enum.utilities';

type StatusType = MemberStatuses | PropertyStatuses | UserStatuses | OrganizationStatuses | UserInvitationStatuses | ChargeStatus | BillingStatuses | PaymentStatus | EmailDeliveryStatus | ExpenseStatus | undefined;

@Component({
  selector: 'ms-status-badge',
  imports: [],
  templateUrl: './status-badge.component.html',
  styleUrl: './status-badge.component.scss',
  standalone: true
})
export class StatusBadgeComponent {
  public status = input.required<StatusType>();

  public isLoading = model<boolean>(true);

  public getFormattedStatus(): string {
    const statusValue = this.status();
    if (!statusValue) {
      return '';
    }
    return EnumUtilities.convertToLabel(statusValue.toString());
  }

  public getBootstrapClasses(): string {
    const statusValue = this.status();

    if (!statusValue) {
      return 'bg-secondary-subtle text-secondary-emphasis';
    }

    const status = statusValue.toString();

    switch (status) {
      // Member / User / Org / Property lifecycle
      case MemberStatuses.Active:
        return 'bg-success-subtle text-success-emphasis';

      case MemberStatuses.Deleted:
        return 'bg-danger-subtle text-danger-emphasis';

      case MemberStatuses.Invited:
      case MemberStatuses.Created:
        return 'bg-info-subtle text-info-emphasis';

      case MemberStatuses.Expired:
      case MemberStatuses.Suspended:
      case MemberStatuses.Deactivated:
      // PropertyStatuses.Inactive, UserStatuses.Inactive and OrganizationStatuses.Inactive all
      // share this string value — a single case covers the "deactivated" state across all three.
      case PropertyStatuses.Inactive:
      case ChargeStatus.PaidLate:
        return 'bg-warning-subtle text-warning-emphasis';

      // ChargeStatus specific
      case ChargeStatus.Open:
        return 'bg-primary-subtle text-primary-emphasis';

      case ChargeStatus.Paid:
        return 'bg-success-subtle text-success-emphasis';

      case ChargeStatus.Late:
        return 'bg-danger-subtle text-danger-emphasis';

      case ChargeStatus.Locked:
        return 'bg-secondary-subtle text-secondary-emphasis';

      case ChargeStatus.Voided:
        return 'bg-dark-subtle text-dark-emphasis';

      // ExpenseStatus specific (ExpenseStatus.Pending and .Voided share the cases above by value)
      case ExpenseStatus.Cleared:
        return 'bg-success-subtle text-success-emphasis';

      case ChargeStatus.Pending:
      case PaymentStatus.RefundPending:
      case PaymentStatus.Canceling:
        return 'bg-warning-subtle text-warning-emphasis';

      // PaymentStatus specific
      case PaymentStatus.Succeeded:
      case PaymentStatus.Marked:
        return 'bg-success-subtle text-success-emphasis';

      case PaymentStatus.Failed:
        return 'bg-danger-subtle text-danger-emphasis';

      case PaymentStatus.Canceled:
        return 'bg-secondary-subtle text-secondary-emphasis';

      case PaymentStatus.Unmarked:
        return 'bg-dark-subtle text-dark-emphasis';

      case PaymentStatus.Refunded:
      case BillingStatuses.Trial:
        return 'bg-info-subtle text-info-emphasis';

      case BillingStatuses.Demo:
        return 'bg-secondary-subtle text-secondary-emphasis';

      // EmailDeliveryStatus specific (EmailDeliveryStatus.Failed shares the danger case above)
      case EmailDeliveryStatus.Sent:
      case EmailDeliveryStatus.Delivered:
        return 'bg-success-subtle text-success-emphasis';

      case EmailDeliveryStatus.Opened:
        return 'bg-info-subtle text-info-emphasis';

      case EmailDeliveryStatus.Bounced:
      case EmailDeliveryStatus.SpamReport:
        return 'bg-danger-subtle text-danger-emphasis';

      case EmailDeliveryStatus.Unsubscribed:
        return 'bg-warning-subtle text-warning-emphasis';

      case EmailDeliveryStatus.Suppressed:
        return 'bg-secondary-subtle text-secondary-emphasis';

      default:
        return 'bg-secondary-subtle text-secondary-emphasis';
    }
  }
}
